import axios from 'axios';
import { normalizeApiError } from './shared/apiError';
import { API_URL, clientAxios } from './shared/clientHttp';
export { API_URL } from './shared/clientHttp';

// export const API_URL = 'http://127.0.0.1:8000';
export const WSS_URL = 'wss://api.planium.tech';

export const APIClientUnreadSnapshot = async () => {
  const res = await clientAxios.get('/main/api/client/cabinet/unread/', { timeout: 15000 });
  return res.data;
};

// ─────────────────────────────────────────────────────────────────────────────
//  Авторизация
//  POST /main/api/client/auth/request-call/
//  POST /main/api/client/auth/verify-code/
//  POST /main/api/client/auth/logout/
// ─────────────────────────────────────────────────────────────────────────────

/** Шаг 1: запросить прозвон
 *  Body: { phone }
 *  → { status: 'ok' } | { status: 'error', detail }
 */
export const APIClientRequestCall = async (phone, referralCode) => {
    try {
        const body = { phone };
        if (referralCode) body.referral_code = referralCode;
        const res = await clientAxios.post('/main/api/client/auth/request-call/', body, { timeout: 20000 });
        return res.data;
    } catch (e) {
        // detail оставляем для обратной совместимости со старым кодом модалок
        const err = normalizeApiError(e, 'Не удалось отправить звонок, попробуйте позже');
        return { status: 'error', detail: err.message, ...err };
    }
};

export const APIClientRequestEmail = async (email, referralCode) => {
    try {
        const body = { email };
        if (referralCode) body.referral_code = referralCode;
        const res = await clientAxios.post('/main/api/client/auth/request-email/', body);
        return res.data;
    } catch (e) {
        const err = normalizeApiError(e, 'Не удалось отправить код на почту');
        return { status: 'error', detail: err.message, ...err };
    }
};

/** Шаг 2: подтвердить код
 *  Body: { phone, code }
 *  → { status: 'ok', token, user: { id, phone, last_name, name, patronymic, full_name, email, date_create } }
 */
export const APIClientVerifyCode = async (contact, code, channel = 'phone') => {
    try {
        const payload = channel === 'email'
            ? { email: contact, code }
            : { phone: contact, code };

        const res = await clientAxios.post('/main/api/client/auth/verify-code/', payload);
        return res.data;
    } catch (e) {
        // Раньше при 502 или отсутствии сети клиент видел «Неверный код»
        // и вводил его заново — теперь текст соответствует причине.
        const err = normalizeApiError(e, 'Неверный код. Проверьте цифры и попробуйте снова.');
        return { status: 'error', detail: err.message, ...err };
    }
};

/** Выход — сбрасывает токен на сервере
 *  Authorization: ClientToken <token>
 */
export const APIClientLogout = async () => {
    try {
        const token = localStorage.getItem('client_token');
        if (token) await axios.post(`${API_URL}/main/api/client/auth/logout/`, {}, {
            headers: { Authorization: `ClientToken ${token}` }, timeout: 15000,
        });
    } catch (_) {}
};

// ─────────────────────────────────────────────────────────────────────────────
//  Профиль
//  GET  /main/api/client/profile/
//  PATCH /main/api/client/profile/update/
// ─────────────────────────────────────────────────────────────────────────────

/** Получить профиль текущего клиента
 *  → { id, phone, last_name, name, patronymic, full_name, email, date_create } | null
 */
export const APIClientGetProfile = async () => {
    const res = await clientAxios.get('/main/api/client/profile/', { timeout: 15000 });
    if (!res.data?.id) throw new Error('Сервер вернул некорректный профиль');
    return res.data;
};

/** Обновить профиль
 *  Body: { last_name?, name?, patronymic?, full_name?(deprecated), email?, phone?, client_type?, ... }
 *  → обновлённый профиль | { status: 'error', detail }
 */
export const APIClientUpdateProfile = async (data) => {
    try {
        const res = await clientAxios.patch('/main/api/client/profile/update/', data, { timeout: 20000 });
        return res.data;
    } catch (e) {
        // fields нужен модалке, чтобы подсветить конкретный инпут,
        // а не выводить пользователю JSON с ошибками сериализатора.
        const err = normalizeApiError(e, 'Не удалось сохранить профиль');
        return { status: 'error', detail: err.message, ...err };
    }
};

// ─────────────────────────────────────────────────────────────────────────────
//  Заявки
//  POST /main/api/client/applications/create/   (без авторизации тоже работает)
//  GET  /main/api/client/applications/          (только авторизованным)
// ─────────────────────────────────────────────────────────────────────────────

// Создать заявку
export const APIClientCreateApplication = async (data) => {
    try {
        const config = data instanceof FormData
            ? { timeout: 600000 }
            : {};
        const res = await clientAxios.post('/main/api/client/applications/create/', data, config);
        return res.data;
    } catch (e) {
        const d = e?.response?.data || {};
        // Создание валидируется сериализатором: ошибки полей приходят объектом
        // { field: [msg], ... } без общего detail — пробрасываем как fieldErrors.
        const isFieldErrors = d && typeof d === 'object' && !Array.isArray(d) && !d.detail;
        return {
            status: 'error',
            detail: typeof d.detail === 'string' ? d.detail : 'Не удалось создать заявку',
            fieldErrors: isFieldErrors ? d : null,
        };
    }
};

// Базовое ТЗ на обмеры (что входит) — публично, без токена
export const APIClientGetBaseSpec = async () => {
    try {
        const res = await clientAxios.get('/main/api/client/base-spec/');
        const d = res.data || {};
        return { title: d.title || null, body: d.body || null, file_url: d.file_url || null };
    } catch (e) {
        return { title: null, body: null, file_url: null };
    }
};

// Короткая заявка
export const APIClientCreateShortApplication = async (data) => {
    try {
        const res = await clientAxios.post('/main/api/client/applications/create-short/', data);
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось создать заявку' };
    }
};

// Публичный расчёт стоимости (калькулятор ЛК, авторасчёт в заявке, доступность обмерщика).
// Тело: { square, city_id, transfer_km, service_ids?, additional_services?, with_availability?, lat?, lng?, custom_city_name? }
export const APIClientCalculatorQuote = async (payload) => {
    try {
        const res = await clientAxios.post('/main/api/client/calculator/quote/', payload);
        return res.data;   // { client_total, services_total, transfer, minimum_applied, breakdown, availability? }
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось рассчитать стоимость' };
    }
};

// JSON остаётся для обычного автосохранения; с планом нужен multipart.
const draftRequestBody = (payload) => {
    if (payload instanceof FormData || !(payload?.plan_file instanceof Blob)) return payload;
    const body = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
        if (value == null) return;
        if (key === 'programs') value.forEach(id => body.append(key, id));
        else if (Array.isArray(value) || (typeof value === 'object' && !(value instanceof Blob))) {
            body.append(key, JSON.stringify(value));
        } else body.append(key, value);
    });
    return body;
};

// ── Черновики заявок клиента (пункт 4) ──
export const APIClientSaveDraft = async (payload) => {
    try {
        const res = await clientAxios.post('/main/api/client/drafts/save/', draftRequestBody(payload));
        return res.data;   // { status:'ok', is_draft:true, draft_id, application }
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось сохранить черновик' };
    }
};

export const APIClientGetDrafts = async () => {
    try {
        const res = await clientAxios.get('/main/api/client/drafts/');
        return Array.isArray(res.data) ? res.data : [];
    } catch (_) {
        return [];
    }
};

export const APIClientGetDraft = async (id) => {
    try {
        const res = await clientAxios.get(`/main/api/client/drafts/${id}/`);
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось загрузить черновик' };
    }
};

export const APIClientFinalizeDraft = async (id, payload = {}) => {
    try {
        const res = await clientAxios.post(`/main/api/client/drafts/${id}/finalize/`, draftRequestBody(payload), {timeout: payload instanceof FormData ? 600000 : 30000});
        return res.data;   // { status:'ok', application_id, chat_id, bitrix_deal_id }
    } catch (e) {
        const data = e?.response?.data || {};
        // 400 с обязательными полями пробрасываем как есть (missing: [{field,label}])
        return { status: 'error', detail: data.detail || 'Не удалось отправить черновик', missing: data.missing || null, fieldErrors: data };
    }
};

export const APIClientDeleteDraft = async (id) => {
    try {
        const res = await clientAxios.delete(`/main/api/client/drafts/${id}/delete/`);
        return res.data;   // { status:'ok', deleted:true }
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось удалить черновик' };
    }
};

// ── Адрес: подсказки (DaData, с учётом города) для модалки создания заявки ──
// POST /main/api/client/suggest-address/  Body: { query, city }
export const APIClientSuggestAddress = async (query, city = '') => {
    try {
        const res = await clientAxios.post('/main/api/client/suggest-address/', { query, city });
        const d = res.data || {};
        return {
            configured: !!d.configured,
            suggestions: Array.isArray(d.suggestions) ? d.suggestions : [],
        };
    } catch (_) {
        return { configured: false, suggestions: [] };
    }
};

// ── Адрес: геокодирование выбранного адреса (подтверждение точки) ──
// POST /main/api/client/geocode/  Body: { address, city }
// → { normalized, latitude, longitude, city, precision_ok, qc_geo, message, configured }
export const APIClientGeocodeAddress = async (address, city = '') => {
    try {
        const res = await clientAxios.post('/main/api/client/geocode/', { address, city });
        return res.data;
    } catch (e) {
        return {
            status: 'error',
            latitude: null, longitude: null, precision_ok: false,
            message: e?.response?.data?.detail || 'Не удалось проверить адрес',
        };
    }
};

// ── Адрес: определение города по геолокации/адресу (кнопка «Определить город») ──
// POST /main/api/client/detect-city/  Body: { latitude, longitude } | { address }
// → { status, city_id, city_name, matched, latitude, longitude, message, configured }
export const APIClientDetectCity = async (payload) => {
    try {
        const res = await clientAxios.post('/main/api/client/detect-city/', payload);
        return res.data;
    } catch (e) {
        return { status: 'error', matched: false,
                 detail: e?.response?.data?.detail || 'Не удалось определить город' };
    }
};

// Мои заявки (нужна авторизация) — без изменений
export const APIClientGetMyApplications = async () => {
    try {
        const res = await clientAxios.get('/main/api/client/applications/');
        return res.data;
    } catch (_) {
        return [];
    }
};

// Мои заявки постранично — для бесконечной прокрутки в ЛК.
// Сервер отдаёт { total, count, results } при наличии limit/offset
// (без них — старый формат-массив, поэтому нормализуем ответ на всякий случай).
export const APIClientGetMyApplicationsPage = async ({ limit = 20, offset = 0, status } = {}) => {
    const params = { limit, offset };
    if (status && status !== 'all') params.status = status;
    const res = await clientAxios.get('/main/api/client/applications/', { params, timeout: 20000 });
    const d = res.data;
    if (Array.isArray(d)) return { results: d, total: d.length, count: d.length };
    if (!Array.isArray(d?.results) || !Number.isFinite(d.total)) {
        throw new Error('Сервер вернул некорректный список заявок');
    }
    return { results: d.results, total: d.total, count: d.results.length };
};

// Агрегаты кабинета: счётчики по статусам + разбивка по городам.
// Считаются на сервере по всем заявкам клиента (без черновиков) — не зависят от пагинации.
export const APIClientGetApplicationsStats = async () => {
    try {
        const res = await clientAxios.get('/main/api/client/applications/stats/');
        const d = res.data || {};
        return {
            total: typeof d.total === 'number' ? d.total : 0,
            by_status: d.by_status || {},
            by_city: Array.isArray(d.by_city) ? d.by_city : [],
        };
    } catch (e) {
        return { status: 'error', total: 0, by_status: {}, by_city: [] };
    }
};

// Получить одну заявку
export const APIClientGetApplication = async (id) => {
    try {
        const res = await clientAxios.get(`/main/api/client/applications/${id}/`);
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось загрузить заявку' };
    }
};

// Сотрудники, которых можно добавить в чат заявки (кого ещё нет в чате)
export const APIClientChatAddable = async (applicationId) => {
    try {
        const res = await clientAxios.get(`/main/api/client/applications/${applicationId}/chat/addable/`);
        return res.data; // { status:'ok', accounts:[{id, full_name, phone}] }
    } catch (e) {
        return { status: 'error', accounts: [], detail: e?.response?.data?.detail || 'Не удалось загрузить список' };
    }
};

// Добавить сотрудника в чат заявки вручную
export const APIClientChatAddMember = async (applicationId, accountId) => {
    try {
        const res = await clientAxios.post(`/main/api/client/applications/${applicationId}/chat/add-member/`, {
            account_id: accountId,
        });
        return res.data; // { status:'ok', added, account } | { status:'error', detail }
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось добавить сотрудника' };
    }
};

// Редактировать заявку
export const APIClientUpdateApplication = async (id, data) => {
    try {
        const config = data instanceof FormData
            ? { timeout: 600000 }
            : {};
        const res = await clientAxios.patch(`/main/api/client/applications/${id}/update/`, data, config);
        return res.data;
    } catch (e) {
        const d = e?.response?.data || {};
        // 400 с обязательными полями пробрасываем как есть (missing: [{field,label}]),
        // чтобы форма подсветила поля (например, контакт после серверного Этапа 1).
        return {
            status: 'error',
            detail: typeof d.detail === 'string' ? d.detail : 'Не удалось сохранить изменения',
            missing: Array.isArray(d.missing) ? d.missing : null,
        };
    }
};

export const APIClientConfirmApplicationEdit = async (id, editId) => {
    try {
        const res = await clientAxios.post(`/main/api/client/applications/${id}/confirm-edit/`, { edit_id: editId });
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Изменения сохранены, но подтверждение не получено. Нажмите «Сохранить изменения» повторно.' };
    }
};

// Отметить заявку как «Не принято» → доработка
export const APIClientRejectApplication = async (id, deliveryId, comment) => {
    try {
        const res = await clientAxios.post(`/main/api/client/applications/${id}/reject/`,
            { delivery_id: deliveryId, comment }, { timeout: 20000 });
        return res.data;
    } catch (e) {
        const err = normalizeApiError(e, 'Не удалось отправить на доработку');
        return { ...err, http_status: err.status, status: 'error', detail: err.message };
    }
};

export const APIClientAcceptApplication = async (id, deliveryId) => {
    try {
        const res = await clientAxios.post(`/main/api/client/applications/${id}/accept/`,
            { delivery_id: deliveryId }, { timeout: 20000 });
        return res.data;
    } catch (e) {
        const err = normalizeApiError(e, 'Не удалось принять заявку');
        return { ...err, http_status: err.status, status: 'error', detail: err.message };
    }
};

// Оценить заявку (после приёмки). Звёзды 0–5, где 0 = роль не оценивать.
// Тело: { surveyor_stars, draftsman_stars, manager_stars, comment }.
export const APIClientRateApplication = async (
    id,
    { surveyor_stars = 0, draftsman_stars = 0, manager_stars = 0, comment = '' } = {},
) => {
    try {
        const res = await clientAxios.post(`/main/api/client/applications/${id}/rate/`, {
            surveyor_stars,
            draftsman_stars,
            manager_stars,
            comment,
        });
        return res.data; // { status:'ok', rating:{...}, can_rate:false } | { status:'error', detail }
    } catch (e) {
        return {
            status: 'error',
            detail: e?.response?.data?.detail || 'Не удалось сохранить оценку',
        };
    }
};

// Загрузить один файл к заявке
export const APIClientUploadApplicationFile = async (applicationId, file, initial = false, editId = null, uploadId = null) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        if (editId) formData.append('edit_id', editId);
        if (uploadId) formData.append('edit_upload_id', uploadId);
        if (initial) formData.append('initial', 'true');  // файл первичной отправки, не правка
        const res = await clientAxios.post(
            `/main/api/client/applications/${applicationId}/upload-file/`,
            formData,
            { timeout: 600000 }
        );
        return res.data;
    } catch (e) {
        const error = normalizeApiError(e, 'Ошибка загрузки файла');
        return { ...error, http_status: error.status, status: 'error', detail: error.message };
    }
};

// Удалить файл из заявки
export const APIClientDeleteApplicationFile = async (applicationId, fileId, editId = null) => {
    try {
        const res = await clientAxios.delete(
            `/main/api/client/applications/${applicationId}/delete-file/${fileId}/`,
            { params: editId ? { edit_id: editId } : {} }
        );
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Ошибка удаления файла' };
    }
};



// Получить список программ для отрисовки
export const APIClientGetPrograms = async () => {
    try {
        const res = await clientAxios.get('/main/api/client/programs/');
        return res.data;   // [{ id: 1, name: "Autocad (.dwg)" }, ...]
    } catch (_) {
        return [];
    }
};

// Запрос городов
export const APIGetCitys = async () => {
    try {
        const response = await clientAxios.get('/bitrix/get-birtrix-citys/');
        return response.data;
    } catch (_) {
        return [];
    }
};


// Получить сообщения чата по ID с пагинацией.
// opts.anchor === 'unread' — открыть окно на границе непрочитанного;
// opts.markRead === false — НЕ отмечать страницу прочитанной при загрузке
// (кабинет сам сообщает о прочтении, по факту показа сообщения на экране).
// Без opts поведение прежнее.
export const APIGetChatMessagesById = async (chatId, token, limit = 30, offset = 0, opts = {}) => {
  try {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (opts.anchor) params.set('anchor', opts.anchor);
    if (opts.markRead === false) params.set('mark_read', '0');
    const response = await clientAxios.get(`/chat/${chatId}/messages/?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};


// Отправка сообщения в чат
export const APISendMessage = async (chatId, formData, token) => {
  try {
    const response = await clientAxios.post(`${API_URL}/chat/${chatId}/send-message/`, formData, {
      timeout: 0, clientSessionToken: token,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};


// ── Голосовые сообщения ─────────────────────────────────────────────────
// blob — записанное аудио (webm/ogg/mp4), meta.duration — секунды,
// meta.waveform — массив пиков (0..100), meta.replyToId — если это ответ.
// onProgress(0..100) — индикатор загрузки, abortSignal — чтобы отменить.
export const APISendVoiceMessage = async (
  chatId, blob, { duration, waveform, replyToId } = {},
  onProgress = null, abortSignal = null,
) => {
  try {
    const fd = new FormData();
    const ext = (blob.type || '').includes('ogg') ? 'ogg'
              : (blob.type || '').includes('mp4') ? 'm4a' : 'webm';
    fd.append('attachments', blob, `voice.${ext}`);
    fd.append('type', 'voice');
    if (duration != null) fd.append('duration', String(duration));
    if (waveform) fd.append('waveform', JSON.stringify(waveform));
    if (replyToId) fd.append('reply_to_id', String(replyToId));

    const res = await clientAxios.post(`/chat/${chatId}/send-message/`, fd, {
      timeout: 120000,
      ...(abortSignal ? { signal: abortSignal } : {}),
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

// Отметить голосовое прослушанным (гасит точку «не прослушано»).
export const APIMarkVoiceListened = async (chatId, messageId) => {
  try {
    const res = await clientAxios.post(`/chat/${chatId}/voice/listened/`, { message_id: messageId });
    return res.data;
  } catch (error) {
    return null;
  }
};


// ── Прочтения ───────────────────────────────────────────────────────────
// Отметить конкретные сообщения прочитанными. Кабинет зовёт это, когда
// сообщение реально показалось на экране, — именно эти отметки сотрудники
// видят у себя как «клиент прочитал».
export const APIMarkMessagesAsRead = async (chatId, messageIds) => {
  try {
    const res = await clientAxios.post(`/chat/${chatId}/mark-read/`, { message_ids: messageIds });
    return res.data;
  } catch (error) {
    return null;
  }
};

// Отметить прочитанным весь чат (кнопка «вниз» в ленте).
export const APIMarkChatReadAll = async (chatId) => {
  try {
    const res = await clientAxios.post(`/chat/${chatId}/read-all/`, {});
    return res.data;
  } catch (error) {
    return error?.response?.data || { error: 'Не удалось отметить чат прочитанным' };
  }
};


// ── Действия над сообщением ─────────────────────────────────────────────
// Все три возвращают тело ошибки, а не бросают: интерфейсу нужно показать
// причину (403 «только своё», 409 «срок правки истёк») текстом.
export const APIDeleteMessage = async (chatId, messageId) => {
  try {
    const res = await clientAxios.delete(`/chat/${chatId}/messages/${messageId}/delete/`);
    return res.data;
  } catch (error) {
    return error?.response?.data || { error: 'Не удалось удалить сообщение' };
  }
};

export const APIEditMessage = async (chatId, messageId, content) => {
  try {
    const res = await clientAxios.patch(`/chat/${chatId}/messages/${messageId}/edit/`, { content });
    return res.data;
  } catch (error) {
    return error?.response?.data || { error: 'Не удалось изменить сообщение' };
  }
};

export const APIToggleMessageReaction = async (chatId, messageId, emoji) => {
  try {
    const res = await clientAxios.post(`/chat/${chatId}/messages/${messageId}/reaction/`, { emoji });
    return res.data;
  } catch (error) {
    return error?.response?.data || { error: 'Не удалось поставить реакцию' };
  }
};


// WebSocket подключение к потоку сообщений чата.
// onStatusChange(status) получает строку: 'connecting' | 'open' | 'error'
export const createWebSocketConnection = (chatId, token, onMessageCallback, onStatusChange) => {
  let socket = null;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let handshakeTimer = null;
  let manuallyClosed = false;

  const HANDSHAKE_TIMEOUT = 8000;  // если рукопожатие не завершилось за 8с — считаем сбоем

  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000;  // 1с, дальше экспоненциально
  const maxReconnectDelay = 15000;  // потолок 15с

  // Коды, при которых переподключаться НЕ нужно (это не сетевой сбой):
  // 1000 — нормальное закрытие, 4001 — невалидный токен, 4003 — нет доступа к чату.
  const NO_RETRY_CODES = [1000, 4001, 4003];

  const connect = () => {
    onStatusChange?.('connecting');
    socket = new WebSocket(`${WSS_URL}/chatws/${chatId}/?token=${token}`);

    // Страховка от «вечного Подключение…»: если сервер/прокси не завершил upgrade,
    // закрываем сокет — сработает onclose и пойдёт переподключение/ошибка.
    handshakeTimer = setTimeout(() => {
      if (socket && socket.readyState !== WebSocket.OPEN) {
        try { socket.close(); } catch (_) {}
      }
    }, HANDSHAKE_TIMEOUT);

    socket.onopen = () => {
      clearTimeout(handshakeTimer);
      reconnectAttempts = 0;
      onStatusChange?.('open');
    };

    socket.onmessage = (e) => {
      try {
        onMessageCallback(JSON.parse(e.data));
      } catch (error) {
        console.error('Ошибка парсинга сообщения чата:', error);
      }
    };

    socket.onclose = (e) => {
      clearTimeout(handshakeTimer);
      if (manuallyClosed) return;                  // закрыли сами — статус не трогаем
      if (NO_RETRY_CODES.includes(e.code)) {       // фатальный код — без переподключения
        onStatusChange?.('error');
        return;
      }
      if (reconnectAttempts < maxReconnectAttempts) {
        const delay = Math.min(baseReconnectDelay * 2 ** reconnectAttempts, maxReconnectDelay);
        reconnectAttempts++;
        onStatusChange?.('connecting');
        reconnectTimer = setTimeout(connect, delay);
      } else {
        onStatusChange?.('error');
      }
    };

    socket.onerror = () => {
      // onerror всегда сопровождается onclose — финальный статус выставит onclose.
    };
  };

  connect();

  // Ручное закрытие: гасим таймер реконнекта и сам сокет.
  const close = () => {
    manuallyClosed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (handshakeTimer) {
      clearTimeout(handshakeTimer);
      handshakeTimer = null;
    }
    if (socket) {
      socket.close(1000);  // нормальное закрытие
      socket = null;
    }
  };

  const isConnected = () => socket && socket.readyState === WebSocket.OPEN;

  return { close, isConnected };
};



// ─────────────────────────────────────────────────────────────────────────────
//  Push-уведомления
// ─────────────────────────────────────────────────────────────────────────────

/** Подписка на push-уведомления */
export const APIClientPushSubscribe = async (subscriptionData) => {
    try {
        const res = await clientAxios.post('/main/api/client/push/subscribe/', subscriptionData);
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Ошибка подписки' };
    }
};

/** Отписка от push */
export const APIClientPushUnsubscribe = async (endpoint) => {
    try {
        const res = await clientAxios.post('/main/api/client/push/unsubscribe/', { endpoint });
        return res.data;
    } catch (_) {
        return null;
    }
};

/** Тестовый push */
export const APIClientPushTest = async () => {
    try {
        const res = await clientAxios.post('/main/api/client/push/test/');
        return res.data;
    } catch (_) {
        return null;
    }
};

/** Активные push-подписки клиента (сколько устройств подключено) */
export const APIClientGetPushSubscriptions = async () => {
    try {
        const res = await clientAxios.get('/main/api/client/push/subscriptions/');
        return Array.isArray(res.data) ? res.data : [];
    } catch (_) {
        return [];
    }
};

/** Получить настройки уведомлений */
export const APIClientGetNotificationSettings = async () => {
    try {
        const res = await clientAxios.get('/main/api/client/notification-settings/');
        return res.data;
    } catch (_) {
        return null;
    }
};

/** Обновить настройки уведомлений */
export const APIClientUpdateNotificationSettings = async (data) => {
    try {
        const res = await clientAxios.patch('/main/api/client/notification-settings/', data);
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Ошибка сохранения' };
    }
};


// Поиск городов по названию (autocomplete)
export const APISearchCities = async (query = '', limit = 10) => {
    try {
        const res = await clientAxios.get('/bitrix/api/search-cities/', {
            params: { q: query, limit },
        });
        return res.data;
    } catch (_) {
        return [];
    }
};


/** Получить мой реф.код и ссылку */
export const APIReferralGetMyCode = async () => {
    try {
        const res = await clientAxios.get('/api/referral/code/');
        return res.data; // { code, link, is_active }
    } catch (_) {
        return null;
    }
};

/** Статистика моих приглашений */
export const APIReferralGetStats = async () => {
    try {
        const res = await clientAxios.get('/api/referral/stats/');
        return res.data; // { total_invited, with_applications, rewarded, invites: [...] }
    } catch (_) {
        return null;
    }
};

/** Баланс + история транзакций */
export const APIReferralGetBalance = async () => {
    try {
        const res = await clientAxios.get('/api/referral/balance/');
        return res.data; // { balance, total_earned, total_spent, transactions: [...] }
    } catch (_) {
        return null;
    }
};

/** Применить чужой реф. код вручную (если не пришёл по ссылке) */
export const APIReferralApplyCode = async (code) => {
    try {
        const res = await clientAxios.post('/api/referral/apply/', { code });
        return res.data; // { status: 'ok' } | { status: 'error', message }
    } catch (e) {
        return { status: 'error', message: e?.response?.data?.message || 'Не удалось применить код' };
    }
};

/** Публичные настройки реф. программы (для отображения условий) */
export const APIReferralGetSettings = async () => {
    try {
        const res = await clientAxios.get('/api/referral/settings/');
        return res.data;
        // {
        //   is_active, reward_trigger, min_application_amount, max_referrals_per_client,
        //   referrer: { percent, min_amount, max_amount },
        //   invitee:  { percent, min_amount, max_amount },
        //   terms_text
        // }
    } catch (_) {
        return null;
    }
};


// ─────────────────────────────────────────────────────────────────────────────
//  Платежи (эквайринг Точка Банк)
//  GET  /main/api/client/payments/
//  GET  /main/api/client/payments/<id>/
//  GET  /main/api/client/payments/application/<appId>/
//  POST /main/api/client/payments/application/<appId>/pay/
//  POST /main/api/client/payments/<id>/check/
// ─────────────────────────────────────────────────────────────────────────────

/** Список платежей клиента. params: { status?, application? } */
export const APIClientGetPayments = async (params = {}) => {
    try {
        const res = await clientAxios.get('/main/api/client/payments/', { params });
        return res.data; // [ {id, amount, currency, status, status_display, payment_url, is_payable, is_paid, ...}, ... ]
    } catch (_) {
        return [];
    }
};

/** Детали одного платежа */
export const APIClientGetPayment = async (id) => {
    try {
        const res = await clientAxios.get(`/main/api/client/payments/${id}/`);
        return res.data;
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось загрузить платёж' };
    }
};

/** Платежи по заявке → { active: <платёж|null>, history: [...] } */
export const APIClientGetApplicationPayment = async (applicationId) => {
    try {
        const res = await clientAxios.get(`/main/api/client/payments/application/${applicationId}/`);
        return res.data;
    } catch (_) {
        return { active: null, history: [] };
    }
};

/** Получить/создать ссылку на оплату заявки (только физлица) */
export const APIClientPayApplication = async (applicationId) => {
    try {
        const res = await clientAxios.post(`/main/api/client/payments/application/${applicationId}/pay/`);
        return res.data; // { status:'ok', payment } | { status:'error', detail, payment? }
    } catch (e) {
        return {
            status: 'error',
            detail: e?.response?.data?.detail || 'Не удалось создать платёж',
            payment: e?.response?.data?.payment || null,
        };
    }
};

/** Проверить статус платежа у Точки → обновлённый объект платежа */
export const APIClientCheckPayment = async (paymentId) => {
    try {
        const res = await clientAxios.post(`/main/api/client/payments/${paymentId}/check/`);
        return res.data; // объект платежа
    } catch (e) {
        return { status: 'error', detail: e?.response?.data?.detail || 'Не удалось проверить платёж' };
    }
};


// ─────────────────────────────────────────────────────────────────────────────
//  Новости (сторис)
//  Авторизацию clientAxios подставляет сам — сервер разбирает ClientToken
//  и отдаёт новости с audience_type = clients | all.
// ─────────────────────────────────────────────────────────────────────────────

const NEWS_BASE = '/main/api/news';

/**
 * События просмотра шлём «в фоне»: ошибка сети не должна ломать проигрывание.
 * keepalive нужен для досыла времени при закрытии вкладки, поэтому здесь
 * используется fetch, а не axios — axios не умеет keepalive.
 */
const newsPost = async (path, body, keepalive = false) => {
    try {
        const token = localStorage.getItem('client_token');
        const res = await fetch(`${API_URL}${NEWS_BASE}${path}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `ClientToken ${token}` } : {}),
            },
            body: JSON.stringify(body || {}),
            keepalive,
        });
        if (!res.ok) return null;
        return await res.json();
    } catch (e) {
        return null;
    }
};

/** Новости к показу сегодня */
export const APIGetNewsFeed = async () => {
    try {
        const res = await clientAxios.get(`${NEWS_BASE}/feed/`);
        return res.data; // { today, count, results: [...] }
    } catch (e) {
        return { count: 0, results: [] };
    }
};

/** Лента сторис для шапки: все актуальные, включая просмотренные (кольца «просмотрено») */
export const APINewsStories = async () => {
    try {
        const res = await clientAxios.get(`${NEWS_BASE}/stories/`);
        return res.data; // { today, count, results: [...] }
    } catch (e) {
        return { count: 0, results: [] };
    }
};

/** Лёгкая проверка «есть ли что показать» */
export const APIGetNewsUnseenCount = async () => {
    try {
        const res = await clientAxios.get(`${NEWS_BASE}/unseen-count/`);
        return res.data;
    } catch (e) {
        return { count: 0, ids: [] };
    }
};

/**
 * Открытие новости в плеере.
 * Плеер ждёт ответа перед первым событием слайда: иначе два параллельных
 * запроса создают две строки просмотра (дубли в статистике).
 */
export const APINewsOpen = (newsId, data) => newsPost(`/${newsId}/open/`, data);

/** Показ слайда. count_view=false — только досыл времени, без нового «показа» */
export const APINewsSlideView = (slideId, data, keepalive = false) =>
    newsPost(`/slides/${slideId}/view/`, data, keepalive);

/** Нажата кнопка «Ок» на последнем слайде */
export const APINewsComplete = (newsId, data) => newsPost(`/${newsId}/complete/`, data);

/** Закрыл, не досмотрев */
export const APINewsDismiss = (newsId, data, keepalive = false) =>
    newsPost(`/${newsId}/dismiss/`, data, keepalive);

/**
 * Досоздать чат по заявке.
 * Страховка на случай, когда чат не создался при оформлении или клиент
 * привязался к заявке позже, чем чат был создан. Идемпотентно.
 * → { status: 'ok', chat_id } | { status: 'error', detail }
 */
export const APIClientEnsureApplicationChat = async (applicationId) => {
    try {
        const res = await clientAxios.post(`/main/api/client/applications/${applicationId}/ensure-chat/`);
        return res.data;
    } catch (e) {
        const err = normalizeApiError(e, 'Не удалось открыть чат');
        return { status: 'error', detail: err.message, ...err };
    }
};


// ── Документы клиента ──
export const documentSharedUrl = (shareToken) =>
    `${API_URL}/documents/shared/${shareToken}/`;

/** Документы текущего клиента → { count, results: [...] } | null */
export const APIClientDocuments = async (params = {}) => {
    try {
        const res = await clientAxios.get('/documents/my/', { params });
        return res.data;
    } catch (_) {
        return null;
    }
};